<template>
  <div class="ai-agents-management">
    <v-container fluid class="px-6 py-4">
      <!-- Page Header -->
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="page-title-section">
            <div class="d-flex align-center mb-2">
              <h1 class="page-title">
                <v-icon large class="vibrant-title-icon mr-3">mdi-robot</v-icon>
                AI Agents Management
                <span class="page-inline-subtitle">Manage AI agents for the Vijan.AI marketplace</span>
              </h1>
              <v-spacer></v-spacer>
              <div class="header-actions">
                <v-btn 
                  color="primary" 
                  to="/ai-agents/create"
                  class="modern-btn"
                  elevation="2"
                >
                  <v-icon left>mdi-plus</v-icon>
                  Add AI Agent
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- AI Agents Table -->
      <v-row>
        <v-col cols="12">
          <v-card class="agents-table-card" elevation="6">
            <v-card-title class="modern-card-title">
              <div class="filters-header-container">
                <div class="filters-single-row">
                  <!-- Title Section -->
                  <div class="table-title-section">
                    <h2 class="table-title">
                      <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
                      All AI Agents
                    </h2>
                    <div class="results-count" v-if="count > 0">
                      <div class="count-display">
                        <span class="count-number">{{ count }}</span>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Clean Filters Section -->
                  <div class="filters-section">
                    <v-text-field
                      v-model="form.search_keyword"
                      placeholder="Search agents..."
                      prepend-inner-icon="mdi-magnify"
                      solo
                      dense
                      clearable
                      hide-details
                      class="filter-search clean-filter"
                      @click:clear="(form.search_keyword = null), (page = 1), getData()"
                      @keydown.enter="(page = 1), getData()"
                    ></v-text-field>
                    
                    <v-select
                      v-model="form.industry_id"
                      :items="industries"
                      placeholder="Industry"
                      solo
                      dense
                      clearable
                      hide-details
                      class="filter-select clean-filter"
                      @input="(page = 1), getData()"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon small color="#64748b">mdi-domain</v-icon>
                      </template>
                    </v-select>
                    
                    <v-select
                      v-model="form.is_active"
                      :items="statuses"
                      placeholder="Status"
                      solo
                      dense
                      clearable
                      hide-details
                      class="filter-select clean-filter"
                      @input="(page = 1), getData()"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon small color="#64748b">mdi-check-circle-outline</v-icon>
                      </template>
                    </v-select>
                    
                    <div class="filter-actions">
                      <v-btn
                        @click="resetFilters()"
                        outlined
                        class="filter-reset-btn clean-btn"
                        small
                        color="#64748b"
                      >
                        <v-icon small left>mdi-refresh</v-icon>
                        Reset
                      </v-btn>
                      
                      <v-btn
                        v-if="agents.length > 0"
                        color="#10b981"
                        @click="generateExcel()"
                        :loading="isExcelLoading"
                        class="filter-export-btn clean-btn"
                        small
                      >
                        <v-icon small left>mdi-download</v-icon>
                        Export
                      </v-btn>
                    </div>
                  </div>
                </div>
              </div>
            </v-card-title>

            <v-card-text class="pa-0">
              <v-data-table
                :headers="headers"
                :items="agents"
                :loading="isLoading"
                :server-items-length="count"
                :items-per-page="rowsPerPage"
                :page="page"
                @update:page="updatePage"
                class="modern-data-table"
                hide-default-footer
              >
              <template v-slot:[`item.sr_no`]="{ index }">
                <span class="serial-number">{{ (page - 1) * rowsPerPage + index + 1 }}</span>
              </template>

              <template v-slot:[`item.is_active`]="{ item }">
                <v-chip
                  :color="item.is_active ? 'green' : 'red'"
                  dark
                  small
                >
                  {{ item.is_active ? 'ACTIVE' : 'INACTIVE' }}
                </v-chip>
              </template>

              <template v-slot:[`item.agent_info`]="{ item }">
                <div class="agent-info">
                  <v-avatar size="40" class="agent-avatar mr-3">
                    <v-img
                      :src="item.logo_url || require('../../assets/sapp-only-logo.png')"
                      alt="Agent Logo"
                    />
                  </v-avatar>
                  <div class="agent-details">
                    <div class="agent-name">{{ item.name || '' }}</div>
                    <div class="agent-description">{{ item.description || '' }}</div>
                  </div>
                </div>
              </template>

              <template v-slot:[`item.industry`]="{ item }">
                <v-chip
                  color="blue"
                  outlined
                  small
                >
                  {{ item.industry?.name || 'N/A' }}
                </v-chip>
              </template>

              <template v-slot:[`item.pricing`]="{ item }">
                <div class="pricing-info">
                  <div class="monthly-price">${{ item.monthly_price || '0' }}/mo</div>
                  <div class="annual-price text-caption">${{ item.annual_price || '0' }}/yr</div>
                </div>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <v-btn
                  icon
                  small
                  :to="`/ai-agents/${encryptIt(item.id)}`"
                  color="info"
                >
                  <v-icon>mdi-eye</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  :to="`/ai-agents/${encryptIt(item.id)}/edit`"
                  color="primary"
                >
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  @click="deleteAgent(item)"
                  color="error"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
              </v-data-table>
              
              <!-- Custom Pagination -->
              <div class="modern-pagination pa-4">
                <v-pagination
                  v-model="page"
                  :length="paginationLength"
                  :total-visible="7"
                  color="primary"
                  @input="getData"
                ></v-pagination>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import axios from "@/axios";

export default {
  name: "AIAgentsPage",
  data() {
    return {
      form: {
        search_keyword: null,
        industry_id: null,
        is_active: null,
      },
      agents: [],
      industries: [],
      headers: [
        { text: 'Sr. No.', value: 'sr_no', sortable: false, width: '80px' },
        { text: 'Status', value: 'is_active', sortable: true, width: '120px' },
        { text: 'Agent Info', value: 'agent_info', sortable: false, width: '300px' },
        { text: 'Industry', value: 'industry', sortable: false, width: '150px' },
        { text: 'Pricing', value: 'pricing', sortable: false, width: '120px' },
        { text: 'Features', value: 'features_count', sortable: false, width: '100px' },
        { text: 'Created', value: 'created_at', sortable: true, width: '120px' },
        { text: 'Actions', value: 'actions', sortable: false, width: '120px' },
      ],
      statuses: [
        { text: 'All', value: null },
        { text: 'Active', value: true },
        { text: 'Inactive', value: false }
      ],
      page: 1,
      rowsPerPage: 15,
      paginationLength: 0,
      count: 0,
      isLoading: false,
      isExcelLoading: false,
    };
  },
  watch: {
    page: "getData",
  },
  mounted() {
    this.getIndustries();
    this.getData();
  },
  methods: {
    async getIndustries() {
      try {
        const response = await axios.get('/industries');
        this.industries = response.data.data.map(industry => ({
          text: industry.name,
          value: industry.id
        }));
        this.industries.unshift({ text: 'All Industries', value: null });
      } catch (error) {
        console.error('Error fetching industries:', error);
        // Fallback to sample industries
        this.industries = [
          { text: 'All Industries', value: null },
          { text: 'Marketing', value: 1 },
          { text: 'Technology', value: 2 },
          { text: 'Finance', value: 3 },
          { text: 'Healthcare', value: 4 },
          { text: 'Education', value: 5 },
          { text: 'Retail', value: 6 }
        ];
      }
    },
    async getData() {
      this.isLoading = true;
      this.form.search_keyword = this.form.search_keyword || "";
      this.form.industry_id = this.form.industry_id || "";
      this.form.is_active = this.form.is_active !== null ? this.form.is_active : "";
      
      let query =
        "page=" +
        this.page +
        "&rowsPerPage=" +
        this.rowsPerPage +
        "&search_keyword=" +
        this.form.search_keyword +
        "&industry_id=" +
        this.form.industry_id +
        "&is_active=" +
        this.form.is_active;

      // Add admin filter for Admin users (not Super Admin)
      if (this.roleName === 'ADMIN' || (this.roleName === 'SUPER ADMIN' && this.company.id != null)) {
        query += "&admin_filter=true";
      }

      try {
        const response = await axios.get(`ai-agents?${query}`);
        this.agents = response.data.data;
        this.count = response.data.count;
        this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
      } catch (error) {
        console.error('Error fetching AI agents:', error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
        // Fallback to sample data including ContentMod AI with Marketing industry
        this.agents = [
          {
            id: 1,
            name: 'ContentMod AI',
            description: 'Advanced AI-powered content moderation platform that automatically detects and filters inappropriate content, spam, and toxicity.',
            monthly_price: 99,
            annual_price: 950,
            industry: 'Marketing',
            industry_id: 1,
            industry_name: 'Marketing',
            industry_color: '#667eea',
            industry_icon: 'mdi-bullhorn',
            is_active: true,
            created_at: new Date().toISOString(),
            logo_url: null,
            features: [
              { id: 1, name: 'Real-time content analysis' },
              { id: 2, name: 'Toxicity detection' },
              { id: 3, name: 'Spam filtering' },
              { id: 4, name: 'Custom moderation rules' },
              { id: 5, name: 'Multi-language support' }
            ]
          },
          {
            id: 2,
            name: 'ChatBot Pro',
            description: 'Intelligent customer service chatbot with advanced NLP capabilities for automated customer support.',
            monthly_price: 149,
            annual_price: 1430,
            industry: 'Technology',
            industry_id: 2,
            industry_name: 'Technology',
            industry_color: '#10b981',
            industry_icon: 'mdi-laptop',
            is_active: true,
            created_at: new Date().toISOString(),
            logo_url: null,
            features: [
              { id: 1, name: 'Natural Language Processing' },
              { id: 2, name: '24/7 Customer Support' },
              { id: 3, name: 'Multi-channel Integration' },
              { id: 4, name: 'Sentiment Analysis' }
            ]
          },
          {
            id: 3,
            name: 'Analytics AI',
            description: 'Advanced business analytics and insights platform with predictive modeling and data visualization.',
            monthly_price: 199,
            annual_price: 1910,
            industry: 'Finance',
            industry_id: 3,
            industry_name: 'Finance',
            industry_color: '#8b5cf6',
            industry_icon: 'mdi-bank',
            is_active: true,
            created_at: new Date().toISOString(),
            logo_url: null,
            features: [
              { id: 1, name: 'Predictive Analytics' },
              { id: 2, name: 'Real-time Dashboards' },
              { id: 3, name: 'Custom Reports' },
              { id: 4, name: 'Data Integration' }
            ]
          },
          {
            id: 4,
            name: 'Email Assistant',
            description: 'Smart email management and automation tool with AI-powered response suggestions and scheduling.',
            monthly_price: 79,
            annual_price: 758,
            industry: 'Marketing',
            industry_id: 1,
            industry_name: 'Marketing',
            industry_color: '#667eea',
            industry_icon: 'mdi-bullhorn',
            is_active: true,
            created_at: new Date().toISOString(),
            logo_url: null,
            features: [
              { id: 1, name: 'Smart Responses' },
              { id: 2, name: 'Email Automation' },
              { id: 3, name: 'Priority Filtering' },
              { id: 4, name: 'Schedule Optimization' }
            ]
          },
          {
            id: 5,
            name: 'Data Processor',
            description: 'Large-scale data processing and transformation platform with machine learning capabilities.',
            monthly_price: 249,
            annual_price: 2390,
            industry: 'Technology',
            industry_id: 2,
            industry_name: 'Technology',
            industry_color: '#10b981',
            industry_icon: 'mdi-laptop',
            is_active: true,
            created_at: new Date().toISOString(),
            logo_url: null,
            features: [
              { id: 1, name: 'Batch Processing' },
              { id: 2, name: 'Real-time Streaming' },
              { id: 3, name: 'Data Transformation' },
              { id: 4, name: 'ML Pipeline Integration' }
            ]
          },
          {
            id: 6,
            name: 'Healthcare AI',
            description: 'Medical diagnosis assistance and patient care optimization platform for healthcare providers.',
            monthly_price: 299,
            annual_price: 2870,
            industry: 'Healthcare',
            industry_id: 4,
            industry_name: 'Healthcare',
            industry_color: '#ef4444',
            industry_icon: 'mdi-hospital',
            is_active: true,
            created_at: new Date().toISOString(),
            logo_url: null,
            features: [
              { id: 1, name: 'Diagnosis Assistance' },
              { id: 2, name: 'Patient Monitoring' },
              { id: 3, name: 'Treatment Recommendations' },
              { id: 4, name: 'HIPAA Compliant' }
            ]
          },
          {
            id: 7,
            name: 'EduBot',
            description: 'Educational AI assistant for personalized learning experiences and student engagement.',
            monthly_price: 89,
            annual_price: 854,
            industry: 'Education',
            industry_id: 5,
            industry_name: 'Education',
            industry_color: '#f97316',
            industry_icon: 'mdi-school',
            is_active: false,
            created_at: new Date().toISOString(),
            logo_url: null,
            features: [
              { id: 1, name: 'Personalized Learning' },
              { id: 2, name: 'Progress Tracking' },
              { id: 3, name: 'Interactive Quizzes' },
              { id: 4, name: 'Content Recommendations' }
            ]
          },
          {
            id: 8,
            name: 'RetailOptimizer',
            description: 'Inventory management and sales optimization AI for retail businesses.',
            monthly_price: 179,
            annual_price: 1720,
            industry: 'Retail',
            industry_id: 6,
            industry_name: 'Retail',
            industry_color: '#ec4899',
            industry_icon: 'mdi-storefront',
            is_active: true,
            created_at: new Date().toISOString(),
            logo_url: null,
            features: [
              { id: 1, name: 'Inventory Forecasting' },
              { id: 2, name: 'Price Optimization' },
              { id: 3, name: 'Customer Behavior Analysis' },
              { id: 4, name: 'Supply Chain Management' }
            ]
          }
        ];
        this.count = this.agents.length;
        this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
      }
      this.isLoading = false;
    },
    updatePage(newPage) {
      this.page = newPage;
      this.getData();
    },
    resetFilters() {
      this.form.search_keyword = null;
      this.form.industry_id = null;
      this.form.is_active = null;
      this.page = 1;
      this.getData();
    },
    async generateExcel() {
      this.isExcelLoading = true;
      try {
        let query =
          "search_keyword=" +
          (this.form.search_keyword || "") +
          "&industry_id=" +
          (this.form.industry_id || "") +
          "&is_active=" +
          (this.form.is_active !== null ? this.form.is_active : "");
        
        const response = await axios.get(`/ai-agents/excel_export?${query}`);
        window.open(`${response.data.data}`, "_blank");
      } catch (error) {
        console.error('Error generating Excel:', error);
      }
      this.isExcelLoading = false;
    },
    async deleteAgent(agent) {
      if (confirm(`Are you sure you want to delete ${agent.name}?`)) {
        try {
          await axios.delete(`/ai-agents/${this.encryptIt(agent.id)}`);
          this.getData();
        } catch (error) {
          console.error('Error deleting agent:', error);
        }
      }
    },
  },
};
</script>

<style scoped>
.ai-agents-management {
  min-height: calc(100vh - 64px);
}

/* Page Header */
.page-header {
  background: white;
  border-radius: 20px;
  padding: 16px 20px;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
  position: relative;
  overflow: hidden;
}

.page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.page-inline-subtitle {
  font-size: 16px;
  color: #64748b;
  font-weight: 400;
  margin-left: 16px;
  display: inline;
}

.page-title-section {
  flex: 1;
  width: 100%;
}

.vibrant-title-icon {
  background: linear-gradient(135deg, #ff6b6b 0%, #4ecdc4 25%, #667eea 50%, #764ba2 75%, #f093fb 100%) !important;
  -webkit-background-clip: text !important;
  background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  font-weight: 900 !important;
  filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
  animation: pulse-glow 3s ease-in-out infinite;
  font-size: 32px !important;
}

@keyframes pulse-glow {
  0%, 100% { 
    filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
    transform: scale(1);
  }
  50% { 
    filter: drop-shadow(0 4px 12px rgba(102, 126, 234, 0.6)) drop-shadow(0 0 20px rgba(255, 107, 107, 0.5));
    transform: scale(1.05);
  }
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
  justify-self: flex-end;
  margin-left: auto;
}

/* AI Agents Table Card */
.agents-table-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  background: white;
  overflow: hidden;
}

.modern-card-title {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid rgba(102, 126, 234, 0.1);
  padding: 24px !important;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.table-title {
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

/* Filters Header Container */
.filters-header-container {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.filters-single-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 24px;
}

.table-title-section {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.results-count {
  display: flex;
  align-items: center;
}

.count-display {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 0 16px;
  height: 40px;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.count-number {
  font-size: 18px;
  font-weight: 700;
  color: white;
  line-height: 1;
}

/* Filters Section */
.filters-section {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  justify-content: flex-end;
}

/* Clean Filter Styling */
.clean-filter {
  margin: 0 !important;
}

.clean-filter >>> .v-input__control {
  min-height: 40px !important;
}

.clean-filter >>> .v-input__control .v-input__slot {
  background: white !important;
  border: 1px solid #d1d5db !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  transition: all 0.2s ease;
  padding: 0 16px !important;
  margin: 0 !important;
}

.clean-filter >>> .v-input__control .v-input__slot:before,
.clean-filter >>> .v-input__control .v-input__slot:after {
  display: none !important;
}

.clean-filter >>> .v-input__control .v-input__slot:hover {
  border-color: #9ca3af !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
}

.clean-filter >>> .v-input--is-focused .v-input__control .v-input__slot {
  border-color: #64748b !important;
  box-shadow: 0 0 0 2px rgba(100, 116, 139, 0.15) !important;
}

/* Remove any fieldset or additional borders */
.clean-filter >>> fieldset {
  display: none !important;
}

.clean-filter >>> .v-input__control:before,
.clean-filter >>> .v-input__control:after {
  display: none !important;
}

/* Search Filter - Wider */
.filter-search.clean-filter {
  width: 280px !important;
  min-width: 280px !important;
}

/* Select Filters */
.filter-select.clean-filter {
  width: 160px !important;
  min-width: 160px !important;
}

/* Filter Actions */
.filter-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  white-space: nowrap;
}

/* Clean Buttons */
.clean-btn {
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
  padding: 0 16px !important;
  height: 40px !important;
  min-width: auto !important;
  transition: all 0.2s ease;
  font-size: 13px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
}

.clean-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15) !important;
}

/* Reset Button */
.filter-reset-btn.clean-btn {
  background: white !important;
  border: 1.5px solid #e2e8f0 !important;
  color: #64748b !important;
}

.filter-reset-btn.clean-btn:hover {
  border-color: #cbd5e1 !important;
  background: #f8fafc !important;
}

/* Export Button */
.filter-export-btn.clean-btn {
  background: #10b981 !important;
  color: white !important;
  border: none !important;
}

.filter-export-btn.clean-btn:hover {
  background: #059669 !important;
}

/* Data Table */
.modern-data-table {
  background: transparent !important;
}

.modern-data-table >>> .v-data-table__wrapper {
  background: white;
}

.modern-data-table >>> thead tr th {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  border: none !important;
  border-right: 1px solid rgba(255, 255, 255, 0.2) !important;
  padding: 16px 12px !important;
}

.modern-data-table >>> thead tr th:last-child {
  border-right: none !important;
}

.modern-data-table >>> tbody tr {
  transition: all 0.3s ease;
}

.modern-data-table >>> tbody tr:hover {
  background: rgba(102, 126, 234, 0.05) !important;
}

.modern-data-table >>> tbody tr td {
  padding: 16px 12px !important;
  border-bottom: 1px solid rgba(102, 126, 234, 0.1) !important;
  border-right: 1px solid rgba(102, 126, 234, 0.1) !important;
}

.modern-data-table >>> tbody tr td:last-child {
  border-right: none !important;
}

/* Serial Number Styling */
.serial-number {
  font-weight: 600;
  font-size: 14px;
  color: #64748b;
  background: rgba(102, 126, 234, 0.08);
  padding: 4px 8px;
  border-radius: 6px;
  display: inline-block;
  min-width: 24px;
  text-align: center;
}

/* Agent Info Styling */
.agent-info {
  display: flex;
  align-items: center;
}

.agent-avatar {
  border: 2px solid rgba(102, 126, 234, 0.1);
}

.agent-name {
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
}

.agent-description {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

/* Pricing Info */
.pricing-info {
  text-align: center;
}

.monthly-price {
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
}

.annual-price {
  color: #64748b;
  font-size: 11px;
}

/* Pagination */
.modern-pagination {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid rgba(102, 126, 234, 0.1);
  display: flex;
  justify-content: center;
}

.modern-pagination >>> .v-pagination__item {
  border-radius: 8px !important;
  font-weight: 500;
}

.modern-pagination >>> .v-pagination__item--active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
}

/* Action Buttons in Table */
.v-btn--icon {
  border-radius: 8px !important;
}

/* Chips in table */
.v-chip {
  border-radius: 8px !important;
  font-weight: 500 !important;
}

/* Header Button Styling */
.modern-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  padding: 0 20px !important;
  height: 44px !important;
  font-size: 14px !important;
  transition: all 0.3s ease;
  min-width: auto !important;
}

.modern-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4) !important;
}

.modern-btn:before {
  background-color: transparent;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .filters-single-row {
    gap: 16px;
  }
  
  .filter-search.clean-filter {
    width: 240px !important;
    min-width: 240px !important;
  }
  
  .filter-select.clean-filter {
    width: 140px !important;
    min-width: 140px !important;
  }
}

@media (max-width: 960px) {
  .filters-single-row {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
  
  .table-title-section {
    justify-content: center;
  }
  
  .filters-section {
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .filter-search.clean-filter,
  .filter-select.clean-filter {
    width: 200px !important;
    min-width: 200px !important;
  }
  
  .filter-actions {
    justify-content: center;
  }
  
  .page-inline-subtitle {
    display: block;
    margin-left: 0;
    margin-top: 4px;
    font-size: 14px;
  }
  
  .header-content {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
  
  .page-title {
    font-size: 22px;
  }
  
  .page-header {
    padding: 14px 18px;
  }
}

@media (max-width: 600px) {
  .filters-section {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filter-search.clean-filter,
  .filter-select.clean-filter {
    width: 100% !important;
    min-width: auto !important;
  }
  
  .filter-actions {
    flex-direction: column;
    gap: 8px;
  }
  
  .clean-btn {
    width: 100%;
  }
  
  .page-header {
    padding: 12px 16px;
  }
  
  .page-title {
    font-size: 20px;
  }
  
  .modern-card-title {
    padding: 16px !important;
    flex-direction: column;
    align-items: stretch;
  }
  
  .table-title {
    font-size: 18px;
    text-align: center;
    margin-bottom: 16px;
  }
}
</style>